import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import "../styles/Contact.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faMap,
  faUser,
  faMessage,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";

import {
  faFacebookF,
  faInstagram,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";

import { useNavigate } from "react-router-dom";

const API_URL = API_ENDPOINTS.CONTACT;

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/dir//Sri+Gnana+Sai+Baba+Temple,+Ragavendra+St,+Venkateswara+Nagar+Extension,+Ambattur,+Chennai,+Tamil+Nadu+600062";

const Contact = () => {
  const navigate = useNavigate();

  /* ================= STATE ================= */

  const [contact, setContact] = useState({
    header: { title: "", subtitle: "" },
    phones: [],
    emails: [],
    address: "",
    mapLink: "",
    socials: {
      facebook: "",
      instagram: "",
      youtube: "",
    },
  });

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ================= FETCH CONTACT ================= */

  const fetchContact = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(API_URL);

      if (res.data) {
        setContact(res.data);
      }
    } catch (error) {
      console.log("FETCH ERROR:", error.message);
      setError("Failed to load contact details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  /* ================= FORM ================= */

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    alert("🙏 Thank you! Your message has been sent successfully.");

    console.log("Form Data:", formData);

    setFormData({
      name: "",
      phone: "",
      email: "",
      message: "",
    });

    navigate("/");
  };

  /* ================= LOADING UI ================= */

  if (loading) {
    return (
      <section className="contact-section">
        <div className="contact-header">
          <h2 className="contact-title">Loading Contact...</h2>
          <p className="contact-subtitle">
            Please wait while we fetch information
          </p>
        </div>
      </section>
    );
  }

  /* ================= ERROR UI ================= */

  if (error) {
    return (
      <section className="contact-section">
        <div className="contact-header">
          <h2 className="contact-title">Error</h2>
          <p className="contact-subtitle">{error}</p>
          <button onClick={fetchContact}>Retry</button>
        </div>
      </section>
    );
  }

  return (
    <section className="contact-section">
      {/* ================= HEADER ================= */}
      <div className="contact-header">
        <h2 className="contact-title">
          {contact.header?.title || "Contact Us"}
        </h2>

        <p className="contact-subtitle">
          {contact.header?.subtitle ||
            "Reach us for poojas, spiritual guidance, and temple information"}
        </p>
      </div>

      <div className="contact-container">
        {/* ================= LEFT INFO ================= */}
        <div className="contact-info">
          {/* PHONE */}
          <div className="info-box">
            <FontAwesomeIcon icon={faPhone} />
            <div>
              <h4>Phone</h4>
              {contact.phones?.map((phone, i) => (
                <p key={i}>
                  <a href={`tel:${phone}`}>{phone}</a>
                </p>
              ))}
            </div>
          </div>

          {/* EMAIL */}
          <div className="info-box">
            <FontAwesomeIcon icon={faEnvelope} />
            <div>
              <h4>Email</h4>
              {contact.emails?.map((email, i) => (
                <p key={i}>
                  <a href={`mailto:${email}`}>{email}</a>
                </p>
              ))}
            </div>
          </div>

          {/* ADDRESS */}
          <div className="info-box">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            <div>
              <h4>Address</h4>
              <p>{contact.address}</p>
            </div>
          </div>

          {/* MAP */}
          <div className="info-box">
            <FontAwesomeIcon icon={faMap} />
            <div>
              <h4>Location</h4>
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on Google Maps
              </a>
            </div>
          </div>

          {/* SOCIAL */}
          <div className="info-box">
            <div>
              <h4>Follow Us</h4>

              <div className="social-row">
                <a
                  href={contact.socials?.facebook}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faFacebookF} />
                </a>

                <a
                  href={contact.socials?.instagram}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faInstagram} />
                </a>

                <a
                  href={contact.socials?.youtube}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faYoutube} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================= RIGHT FORM ================= */}
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="input-box">
            <FontAwesomeIcon icon={faUser} />
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="input-box">
            <FontAwesomeIcon icon={faPhone} />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>

          <div className="input-box">
            <FontAwesomeIcon icon={faEnvelope} />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="input-box textarea-box">
            <FontAwesomeIcon icon={faMessage} />
            <textarea
              name="message"
              placeholder="Your Message"
              rows="5"
              value={formData.message}
              onChange={handleChange}
              required
            />
          </div>

          <button type="submit" className="submit-btn">
            <FontAwesomeIcon icon={faPaperPlane} />
            Send Message
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;